import cx from 'classnames'

import VidyardModal from 'components/VidyardModal'
import * as FileAPI from 'app/api/FileAPI'
import { Editor } from '@tinymce/tinymce-react'

import './style.styl'

const CALENDAR_INJECTION_STYLES = `
  .calendar-injection {
    height: 158px;
    width: 350px;
  }
  .calendar-injection ~ .calendar-injection {
    height: auto;
    width: auto;
  }
  .calendar-preview::after {
    background-image: url(https://s3.amazonaws.com/drift-prod-static/assets/calendar-injection.png);
    z-index: 4000;
    position: absolute;
    height: 158px;
    width: 350px;
    content: " ";
    left: 8px;
    visibility: visible;
  }
`

// bg is color-violet
const TINY_INLINE_STYLE_HIGHLIGHTING = `
  .drift-editor-tag {
    display: inline-block;
    border: 1px solid #F2F6F8;
    background-color: rgba(112,67,213,0.2);
    color: #7043d5;
    border-radius: 2px;
    padding: 2px 3px;
  }
  .mce-content-body *[contentEditable=false][data-mce-selected] {
    color: #394F5A;
    border: 1px solid #B5D2E1;
    outline: none !important;
  }
`

const uploadToDrift = (file, success, failure) => {
  return FileAPI.upload(file.blob()).then(res => {
    success(res.url)
  })
}

const tinyFilePicker = (cb, value) => {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', 'image/*')
  input.onchange = function() {
    const file = this.files[0]
    const reader = new FileReader()
    reader.onload = function() {
      const id = `blobid${new Date().getTime()}`
      const blobCache = window.tinymce.activeEditor.editorUpload.blobCache
      const base64 = reader.result.split(',')[1]
      const blobInfo = blobCache.create(id, file, base64)
      blobCache.add(blobInfo)
      cb(blobInfo.blobUri(), { title: file.name })
    }
    reader.readAsDataURL(file)
  }
  input.click()
}

const insertSignature = () => {
  const signatureSpan = ` <span class="drift-editor-tag mceNonEditable">{{ sender.email_signature }}</span> `
  const editor = window.tinymce.activeEditor
  editor.insertContent(signatureSpan)
}

const makeInsertVidyardHandler = (setVidyardModalOpen) => {
  const editor = window.tinymce.activeEditor

  return (embed) => {
    // I've only ever seen the thumbnail url start with //, never with a protocol
    const thumbnailUrl = embed.thumbnail.startsWith('https') ? embed.thumbnail : `https:${embed.thumbnail}`
    const anchor = `<div><img src=${thumbnailUrl} width="400" /><br /><a href=${embed.sharing_page}>${embed.sharing_page}</a></div>`
    editor.insertContent(anchor)
    setVidyardModalOpen(false)
  }
}

const insertOneClickScheduling = () => {
  const oneClickSchedulingSpan = ` <span class="drift-editor-tag mceNonEditable">{{ sender.upcoming_availability }}</span> `
  const editor = window.tinymce.activeEditor
  editor.insertContent(oneClickSchedulingSpan)
}

const returnUrlAsRecieved = (url, node, on_save, name) => url

const buildMenuItems = ({
  showTokenPicker,
  showPersonalization,
  showSignature,
  showCalendarLinkPicker,
  showCtaInsert,
  showCtaButtonModal,
  initialContent,
  setVidyardModalOpen,
  isOutboundSequencePlaybook,
  showMeetingInsert,
  isVidyardEnabled,
}) => {
  const menu = []

  if (showPersonalization)
    menu.push({
      text: 'Personalization',
      onclick: showTokenPicker
    })

  if (showSignature)
    menu.push({
      text: 'Email Signature',
      onclick: insertSignature
    })

  if (showCtaInsert) {
    const alreadyHasButton = !!initialContent && initialContent.includes('data-drift-bui')
    const buttonText = alreadyHasButton ? 'Edit Button' : 'Button'
    menu.push({
      text: buttonText,
      onclick: showCtaButtonModal
    })
  }

  if (isVidyardEnabled) {
    menu.push({
      text: 'Video from Vidyard',
      onclick: () => setVidyardModalOpen(true)
    })
  }

  if (showMeetingInsert) {
    if (isOutboundSequencePlaybook) {
      menu.push({
        text: 'Next Meeting Times',
        onclick: () => insertOneClickScheduling()
      })
    }
    menu.push({
      text: 'Calendar Link',
      onclick: showCalendarLinkPicker
    })
  }

  return menu
}

const TinyEditor = ({
  initialContent,
  onChange,
  showTokenPicker,
  showCalendarLinkPicker,
  actions: { showCtaButtonModal },
  showPersonalization = true,
  showDriftAttributes = true,
  showSignature = true,
  noOuterBorder = false,
  showCtaInsert = false,
  noHighlighting = false,
  isOutboundSequencePlaybook = false,
  showMeetingInsert = true,
  isVidyardEnabled,
  vidyardModalOpen,
  setVidyardModalOpen,
  setShowTripwire,
  hasMeetings,
  includeAlignment = true,
  includeColorAndSize = false,
}) => {
  const insertMenuItems = buildMenuItems({
    showTokenPicker,
    showPersonalization,
    showSignature,
    showCalendarLinkPicker,
    showCtaInsert,
    showCtaButtonModal,
    initialContent,
    setShowTripwire,
    hasMeetings,
    isVidyardEnabled,
    setVidyardModalOpen,
    isOutboundSequencePlaybook,
    showMeetingInsert,
  })
  const alignment = includeAlignment ? '| alignleft aligncenter alignright ' : ''
  const sizeAndColor = includeColorAndSize ? '| fontsizeselect forecolor ' : ''
  return (
    <div className={cx('tiny-editor-container', { 'tiny-editor-container-without-border': noOuterBorder })}>
      <div>
        <VidyardModal
          onClosed={() => setVidyardModalOpen(false)}
          onVideoInserted={makeInsertVidyardHandler(setVidyardModalOpen)}
          insertFull={true}
          isOpen={vidyardModalOpen}
        />
      </div>
      <Editor
        value={initialContent}
        apiKey="nozb66xx8bd4ng55xzewb7obn09fln14zbh0rjyi4ip3rbf7"
        onEditorChange={onChange}
        init={{
          setup: editor => {
            if (showDriftAttributes && insertMenuItems.length !== 0) {
              editor.addButton('driftattrs', {
                type: 'menubutton',
                text: 'Insert',
                icon: false,
                style: 'max-height: 24px;',
                menu: insertMenuItems
              })
            }
          },
          paste_webkit_styles: 'all',
          content_style: CALENDAR_INJECTION_STYLES + (noHighlighting ? '' : TINY_INLINE_STYLE_HIGHLIGHTING),
          height: 350,
          file_picker_types: 'image',
          image_title: false,
          browser_spellcheck: true,
          plugins: 'autoresize lists autolink link image preview paste noneditable code textcolor',
          toolbar: `undo redo | fontselect bold italic | numlist bullist ${alignment}${sizeAndColor}| link image | code | driftattrs`,
          file_picker_callback: tinyFilePicker,
          fontsize_formats: '10pt 12pt 14pt 24pt',
          images_upload_handler: uploadToDrift,
          default_link_target: '_blank',
          paste_data_images: true,
          forced_root_block: 'div',
          // tinymce likes to dynamically change the url, this overrides that and returns the url excatly as it is inputted
          urlconverter_callback: returnUrlAsRecieved,
          remove_script_host: false,
          branding: false,
          menubar: false,
        }}
      />
    </div>
  )
}

export default TinyEditor



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/TinyEditor/component.js